import React, { useEffect, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { DescriptedLabel, Input, Password, BaseButton } from 'iebt-zing-storybook'
import { IHotsiteEvaluatorRegisterForm, updateRegisterForm, store, updateEvaluatorRegisterForm, resetEvaluatorRegisterForm } from 'store'
import _ from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { HotsiteFormMain } from '../..'
import { trans } from 'sharedKernel/i18n'
import { DROPDOWN_BRAZIL_STATES, EVALUATOR_TYPES, INPUT_MASK_FORMAT } from 'sharedKernel/constants'
import { RegisterActions } from '../FormActions'

import {
  handleFormValidity,
  handlePasswordAndConfirmPasswordEquality, ifNotEmptyValidatePassword
} from '../FormActions/validations'
import { IFormFieldProperties } from '../../contracts'
import { ifNotEmptyValidateCEP, ifNotEmptyValidateEmail, ifNotEmptyValidateMobile, isEmptyField } from '../../validations'
import { getAddressByZIP } from 'sharedKernel/api/utils/cep'
import { showToast } from 'components/toast'
import { IViaCepServiceAddressResponse } from 'sharedKernel/api/utils/cep/contract'
import { applyMaskIfNotEmpty, applyTitleOnError } from 'sharedKernel/handlers'
import { regexPatterns } from 'sharedKernel/regex'
import { passwordAcceptance } from 'components/Password/Rules'
import { FormTitle, PasswordAcceptance, SectionTitle, StepButtonsWrapper, StepTransition } from './style'
import { LoginButton } from '../FormActions/Login'

export interface HotsiteRegisterFormProps {
  registerFormStateProps: IHotsiteEvaluatorRegisterForm
  registerUrl: string
  userType: string
}

export const HotsiteRegisterForm = ({ registerFormStateProps, registerUrl, userType }: HotsiteRegisterFormProps): JSX.Element => {
  const registerFormState = { ...registerFormStateProps }
  const [step, setStep] = useState(0)
  const [inTransition, setInTransition] = useState(false)
  const [outTransition, setOutTransition] = useState(false)
  const [showOtherEvaluatorType, setShowOtherEvaluatorType] = useState(false)
  const passwordAcceptanceText = passwordAcceptance()

  const labels = {
    formTitleResolver: trans('Cadastre-se e faça parte da comunidade'),
    formTitleEvaluator: trans('Cadastre-se e seja um avaliador de soluções'),
    startupName: trans('Nome da Startup/Projeto'),
    startupNamePlaceholder: trans('Insira o nome da sua empresa'),
    contactName: trans('Nome Completo'),
    namePlaceholder: trans('Insira seu nome completo'),
    email: trans('E-mail institucional'),
    emailPlaceholder: trans('Insira seu e-mail'),
    phone: trans('Número para contato'),
    phonePlaceholder: trans('Insira seu número de celular'),
    pitch: trans('Descrição para um pitch'),
    cep: trans('CEP'),
    cepSearch: trans('Não sabe seu CEP?'),
    cepPlaceholder: trans('Insira seu cep'),
    cnpj: trans('CNPJ'),
    lineOne: trans('Endereço'),
    lineOnePlaceholder: trans('Insira seu endereço'),
    addressLineOneNumber: trans('Número'),
    addressLineOneNumberPlaceholder: trans('Insira o número'),
    lineTwo: trans('Complemento'),
    lineTwoPlaceholder: trans('Insira o complemento'),
    city: trans('Cidade'),
    cityPlaceholder: trans('Insira sua cidade'),
    state: trans('Estado'),
    country: trans('País'),
    startupWebsite: trans('Site da startup/projeto'),
    password: trans('Senha'),
    passwordPlaceholder: trans('Insira sua senha'),
    passwordConfirmation: trans('Confirmação de senha'),
    passwordConfirmationPlaceholder: trans('Confirme sua senha'),
    titleMessage: trans('Preencha este campo'),
    passwordAcceptance: trans('Sua senha deve conter no mínimo 8 caracteres, uma letra maiúscula, uma letra minúscula, um número, um caracter especial'),
    next: trans('Próximo'),
    back: trans('Voltar')
  }

  useEffect(() => {
    return () => {
      store.dispatch(resetEvaluatorRegisterForm())
    }
  }, [])

  const handleChange = (value, key): void => {
    registerFormState.form[key].value = value
    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
  }

  const handleLineOneChange = (value, key): void => {
    handleChange(value, key)
  }

  const passwordAndConfirmPasswordValidations = (currentPassword: IFormFieldProperties): boolean => {
    if (ifNotEmptyValidatePassword(currentPassword)) {
      return handlePasswordAndConfirmPasswordEquality(
        registerFormState?.form.password,
        registerFormState?.form.confirmPassword
      )
    }
    return false
  }

  const handleZIPActions = async (zipcode: IFormFieldProperties): Promise<void> => {
    if (ifNotEmptyValidateCEP(zipcode)) {
      await autoFillAddressByZIP(registerFormState.form.zipcode.value.replace(regexPatterns.clearMaskedNumbers, ''))
    }
  }

  const autoFillAddressByZIP = async (zipcode: string): Promise<void> => {
    await getAddressByZIP(zipcode)
      .then((response) => {
        fillAddress(response)
      }).catch((error) => {
        showToast('error', error)
      })
  }

  const fillAddress = (address: IViaCepServiceAddressResponse): void => {
    handleChange(address.uf, 'state')
    handleLineOneChange(address.logradouro, 'lineOne')
    handleChange(address.localidade, 'city')
  }

  const isValidSection = (section: number): boolean => {
    let isValid = true
    switch (section) {
      case 0:
        if (registerFormState.form.contactName.isInvalid || registerFormState.form.contactName.value === '') isValid = false
        if (registerFormState.form.email.isInvalid || registerFormState.form.email.value === '') isValid = false
        if (registerFormState.form.phone.isInvalid || registerFormState.form.phone.value === '') isValid = false
        if (registerFormState.form.lineOne.isInvalid || registerFormState.form.lineOne.value === '') isValid = false
        if (registerFormState.form.number.isInvalid || registerFormState.form.number.value === '') isValid = false
        if (registerFormState.form.city.isInvalid || registerFormState.form.city.value === '') isValid = false
        if (registerFormState.form.state.isInvalid || registerFormState.form.state.value === '') isValid = false
        break
      case 1:
        if (registerFormState.form.evaluatorType.isInvalid || registerFormState.form.evaluatorType.value === '') isValid = false
        if (showOtherEvaluatorType && (registerFormState.form.otherEvaluatorType.isInvalid || registerFormState.form.otherEvaluatorType.value === '')) isValid = false
        if (registerFormState.form.evaluatorRole.isInvalid || registerFormState.form.evaluatorRole.value === '') isValid = false
        if (registerFormState.form.evaluatorInstitution.isInvalid || registerFormState.form.evaluatorInstitution.value === '') isValid = false
        if (registerFormState.form.evaluatorDepartment.isInvalid || registerFormState.form.evaluatorDepartment.value === '') isValid = false
        if (registerFormState.form.evaluatorEducation.isInvalid || registerFormState.form.evaluatorEducation.value === '') isValid = false
        break
      case 2:
        if (registerFormState.form.password.isInvalid || registerFormState.form.password.value === '') isValid = false
        if (registerFormState.form.confirmPassword.isInvalid || registerFormState.form.confirmPassword.value === '') isValid = false
        break
    }
    return isValid
  }

  const nextStep = (): void => {
    if (isValidSection(step)) {
      setInTransition(true)
      setTimeout(() => {
        setStep(step + 1)
        setInTransition(false)
      }, 500)
    }
  }

  const backStep = (): void => {
    setOutTransition(true)
    setTimeout(() => {
      setStep(step - 1)
      setOutTransition(false)
    }, 500)
  }

  const renderSection = (): JSX.Element => {
    switch (step) {
      case 0:
        return (
          <>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='contactName'
                  maxLength={60}
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.contactName.message, labels.titleMessage)}
                  label={`${labels.contactName}`}
                  placeholder={labels.namePlaceholder}
                  value={registerFormState?.form.contactName.value}
                  isInvalid={registerFormState?.form.contactName.isInvalid}
                  notValidFieldMessage={registerFormState?.form.contactName.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'contactName')
                  }}
                  onBlur={() => {
                    isEmptyField(registerFormState.form.contactName)
                    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                  }}
                ></Input>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='email'
                  maxLength={60}
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.email.message, labels.titleMessage)}
                  label={`${labels.email}`}
                  placeholder={labels.emailPlaceholder}
                  value={registerFormState?.form.email.value}
                  isInvalid={registerFormState?.form.email.isInvalid}
                  notValidFieldMessage={registerFormState?.form.email.message}
                  onChange={(event) => {
                    handleChange(event.target.value.trim(), 'email')
                  }}
                  onBlur={() => {
                    ifNotEmptyValidateEmail(registerFormState?.form.email)
                    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                  }}
                ></Input>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='phone'
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.phone.message, labels.titleMessage)}
                  label={`${labels.phone}`}
                  placeholder={labels.phonePlaceholder}
                  value={registerFormState?.form.phone.value}
                  code="masked"
                  mask={applyMaskIfNotEmpty(registerFormState?.form.phone.value, INPUT_MASK_FORMAT.MOBILE)}
                  isInvalid={registerFormState?.form.phone.isInvalid}
                  notValidFieldMessage={registerFormState?.form.phone.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'phone')
                  }}
                  onBlur={() => {
                    ifNotEmptyValidateMobile(registerFormState?.form.phone)
                    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                  }}
                ></Input>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={4}>
                <Input
                  id='zipcode'
                  title={applyTitleOnError(registerFormState?.form.zipcode.message, labels.titleMessage)}
                  label={`${labels.cep}`}
                  code='masked'
                  mask={applyMaskIfNotEmpty(registerFormState?.form.zipcode.value, INPUT_MASK_FORMAT.CEP)}
                  placeholder={labels.cepPlaceholder}
                  value={registerFormState?.form.zipcode.value}
                  isInvalid={registerFormState?.form.zipcode.isInvalid}
                  notValidFieldMessage={registerFormState?.form.zipcode.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'zipcode')
                    handleZIPActions(registerFormState?.form.zipcode)
                  }}
                ></Input>
                <a className="text-decoration-none" href="https://www2.correios.com.br/sistemas/buscacep/buscaCep.cfm" target="_blank">
                  {labels.cepSearch}
                </a>
              </Col>
              <Col lg={8}>
                <Input
                  id='lineOne'
                  maxLength={100}
                  isRequired={true}
                  label={`${labels.lineOne}`}
                  placeholder={labels.lineOnePlaceholder}
                  value={registerFormState.form.lineOne.value}
                  isInvalid={registerFormState.form.lineOne.isInvalid}
                  notValidFieldMessage={registerFormState.form.lineOne.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'lineOne')
                  }}
                  onBlur={() => {
                    isEmptyField(registerFormState.form.lineOne)
                    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                  }}
                ></Input>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='number'
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.number.message, labels.titleMessage)}
                  label={`${labels.addressLineOneNumber}`}
                  placeholder={labels.addressLineOneNumberPlaceholder}
                  value={registerFormState?.form.number.value}
                  isInvalid={registerFormState?.form.number.isInvalid}
                  notValidFieldMessage={registerFormState?.form.number.message}
                  onChange={(event) => {
                    handleChange(event.target.value.trim(), 'number')
                  }}
                  onBlur={() => {
                    isEmptyField(registerFormState?.form.number)
                    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                  }}
                ></Input>
              </Col>
              <Col lg={true}>
                <Input
                  id='lineTwo'
                  maxLength={100}
                  label={`${labels.lineTwo}`}
                  placeholder={labels.lineTwoPlaceholder}
                  value={registerFormState?.form.lineTwo.value}
                  isInvalid={registerFormState?.form.lineTwo.isInvalid}
                  notValidFieldMessage={registerFormState?.form.lineTwo.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'lineTwo')
                  }}
                ></Input>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={8}>
                <Input
                  id='city'
                  maxLength={60}
                  isRequired={true}
                  label={`${labels.city}`}
                  placeholder={labels.cityPlaceholder}
                  value={registerFormState?.form.city.value}
                  isInvalid={registerFormState?.form.city.isInvalid}
                  notValidFieldMessage={registerFormState?.form.city.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'city')
                  }}
                  onBlur={() => {
                    isEmptyField(registerFormState.form.city)
                    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                  }}
                ></Input>
              </Col>
              <Col lg={4}>
                <DescriptedLabel>{`${labels.state}`}</DescriptedLabel><span style={{ color: 'red' }}> *</span>
                <Form.Select
                  id='state'
                  value={registerFormState?.form.state.value}
                  onChange={(event) => {
                    handleChange(event.target.value, 'state')
                  }}
                >
                  {DROPDOWN_BRAZIL_STATES.map((option, index) => {
                    return (
                      <option key={index} value={option.value}>
                        {option.label}
                      </option>
                    )
                  })}
                </Form.Select>
              </Col>
            </Row>
          </>
        )
      case 1:
        return (
          <>
            <Row className='my-2'>
              <Col lg={true}>
                <DescriptedLabel>{'Tipo de Avaliador'}</DescriptedLabel><span style={{ color: 'red' }}> *</span>
                <Form.Select
                  id='evaluatorType'
                  value={registerFormState?.form.evaluatorType.value}
                  onChange={(event) => {
                    if (event.target.value === 'Outro') {
                      setShowOtherEvaluatorType(true)
                      handleChange(event.target.value, 'evaluatorType')
                    } else {
                      setShowOtherEvaluatorType(false)
                      handleChange('', 'otherEvaluatorType')
                    }
                    handleChange(event.target.value, 'evaluatorType')
                    isEmptyField(registerFormState.form.evaluatorType)
                  }}
                  onBlur={() => {
                    isEmptyField(registerFormState.form.evaluatorType)
                    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                  }}
                >
                  {EVALUATOR_TYPES.map((option, index) => {
                    return (
                      <option key={index} value={option.value}>
                        {option.label}
                      </option>
                    )
                  })}
                </Form.Select>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='otherEvaluatorType'
                  maxLength={60}
                  isRequired={showOtherEvaluatorType}
                  title={applyTitleOnError(registerFormState?.form.otherEvaluatorType?.message, labels.titleMessage)}
                  label={'Outro'}
                  placeholder={'Insira o tipo de avaliador'}
                  value={registerFormState?.form.otherEvaluatorType?.value}
                  isInvalid={registerFormState?.form.otherEvaluatorType?.isInvalid}
                  notValidFieldMessage={registerFormState?.form.otherEvaluatorType?.message}
                  disabled={!showOtherEvaluatorType}
                  onChange={(event) => {
                    handleChange(event.target.value, 'otherEvaluatorType')
                    if (showOtherEvaluatorType) {
                      handleChange(event.target.value, 'evaluatorType')
                    }
                  }}
                  onBlur={() => {
                    if (showOtherEvaluatorType) {
                      isEmptyField(registerFormState.form.otherEvaluatorType)
                      store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                    }
                  }}
                ></Input>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='evaluatorRole'
                  maxLength={100}
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.evaluatorRole.message, labels.titleMessage)}
                  label={'Cargo/Função'}
                  placeholder={'Insira seu cargo/função'}
                  value={registerFormState?.form.evaluatorRole.value}
                  isInvalid={registerFormState?.form.evaluatorRole.isInvalid}
                  notValidFieldMessage={registerFormState?.form.evaluatorRole.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'evaluatorRole')
                  }}
                  onBlur={() => {
                    isEmptyField(registerFormState.form.evaluatorRole)
                    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                  }}
                ></Input>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='evaluatorInstitution'
                  maxLength={100}
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.evaluatorInstitution.message, labels.titleMessage)}
                  label={'Instituição ou Empresa'}
                  placeholder={'Insira o nome da instituição ou empresa'}
                  value={registerFormState?.form.evaluatorInstitution.value}
                  isInvalid={registerFormState?.form.evaluatorInstitution.isInvalid}
                  notValidFieldMessage={registerFormState?.form.evaluatorInstitution.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'evaluatorInstitution')
                  }}
                  onBlur={() => {
                    isEmptyField(registerFormState.form.evaluatorInstitution)
                    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                  }}
                ></Input>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='evaluatorDepartment'
                  maxLength={60}
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.evaluatorDepartment.message, labels.titleMessage)}
                  label={'Nome da Unidade'}
                  placeholder={'Insira o nome da sua área/lotação'}
                  value={registerFormState?.form.evaluatorDepartment.value}
                  isInvalid={registerFormState?.form.evaluatorDepartment.isInvalid}
                  notValidFieldMessage={registerFormState?.form.evaluatorDepartment.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'evaluatorDepartment')
                  }}
                  onBlur={() => {
                    isEmptyField(registerFormState.form.evaluatorDepartment)
                    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                  }}
                ></Input>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='evaluatorEducation'
                  maxLength={100}
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.evaluatorEducation.message, labels.titleMessage)}
                  label={'Formação Acadêmica Principal'}
                  placeholder={'Insira o nome da sua formação acadêmica principal'}
                  value={registerFormState?.form.evaluatorEducation.value}
                  isInvalid={registerFormState?.form.evaluatorEducation.isInvalid}
                  notValidFieldMessage={registerFormState?.form.evaluatorEducation.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'evaluatorEducation')
                  }}
                  onBlur={() => {
                    isEmptyField(registerFormState.form.evaluatorEducation)
                    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                  }}
                ></Input>
              </Col>
            </Row>
          </>
        )
      case 2:
        return (
          <>
            <Row className='my-2'>
              <Col lg={true}>
                <Row>
                  <Password
                    id='password'
                    isRequired={true}
                    title={applyTitleOnError(registerFormState?.form.password.message, labels.titleMessage)}
                    label={`${labels.password}`}
                    description={passwordAcceptanceText}
                    placeholder={labels.passwordPlaceholder}
                    value={registerFormState?.form.password.value}
                    isInvalid={registerFormState?.form.password.isInvalid}
                    notValidFieldMessage={registerFormState?.form.password.message}
                    onChange={(event) => {
                      handleChange(event.target.value.trim(), 'password')
                    }}
                    onBlur={() => {
                      passwordAndConfirmPasswordValidations(registerFormState?.form.password)
                      store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                    }}
                  ></Password>
                </Row>
              </Col>
              <Col lg={true}>
                <Password
                  id='confirmPassword'
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.confirmPassword.message, labels.titleMessage)}
                  label={`${labels.passwordConfirmation}`}
                  description={passwordAcceptanceText}
                  placeholder={labels.passwordConfirmationPlaceholder}
                  value={registerFormState?.form.confirmPassword.value}
                  isInvalid={registerFormState?.form.confirmPassword.isInvalid}
                  notValidFieldMessage={registerFormState?.form.confirmPassword.message}
                  onChange={(event) => {
                    handleChange(event.target.value.trim(), 'confirmPassword')
                  }}
                  onBlur={() => {
                    passwordAndConfirmPasswordValidations(registerFormState?.form.confirmPassword)
                    store.dispatch(updateEvaluatorRegisterForm(handleFormValidity(registerFormState)))
                  }}
                ></Password>
              </Col>
            </Row>
            <Row>
              <PasswordAcceptance isInvalid={registerFormState?.form.password.isInvalid}>{labels.passwordAcceptance}</PasswordAcceptance>
            </Row>
          </>
        )
      default:
        return <></>
    }
  }

  return (
    <>
      <HotsiteFormMain formTitle={userType === 'resolver' ? labels.formTitleResolver : labels.formTitleEvaluator}>
        {step === 0 && (
          <>
            <FormTitle>{labels.formTitleEvaluator}</FormTitle>
            <hr />
          </>
        )}
        <StepTransition className={`${inTransition ? 'enter' : ''} ${outTransition ? 'exit' : ''}`}>
          <SectionTitle hasIcon={step > 0}>
            {step > 0 && (
              <a onClick={backStep} title={labels.back} className='cursor-pointer'>
                <FontAwesomeIcon className="" size='1x' icon={['fas', 'arrow-alt-circle-left']} />
              </a>
            )}
            <span>
              {step === 0 ? 'Informações Gerais' : step === 1 ? 'Sobre o perfil de avaliador' : 'Segurança'}
            </span>
          </SectionTitle>
          {renderSection()}
        </StepTransition>
        <StepButtonsWrapper className='px-2 mt-2'>
          {step === 2
            ? (
              <>
                <RegisterActions registerUrl={registerUrl} userType={userType} registerFormState={registerFormState} registerType='Evaluator' />
              </>
              )
            : (
              <BaseButton onClick={nextStep} size="big" btnContent={labels.next} variant="primary-invert" />
              )}
        </StepButtonsWrapper>
        <hr className='mt-4' />
        <LoginButton />
      </HotsiteFormMain>
    </>
  )
}
